import React, { FC, useEffect, useState } from 'react'
import { DocumentData } from '../../../../lib/api/types/document.interfaces'
import { Button, Dialog } from '../../../ui'
import { ManageHistory } from '@mui/icons-material'
import {
	useFile,
	useRollbackObjectVersion,
} from '../../../../hooks/api/documentHooks'
import FileFormatIcon from '../../../common/FileFormatIcon'
import FileVersionsTable from './components/FileVersionsTable'
import LockerLoader from '../../../common/LockerLoader'
import { useMediaQuery } from 'react-responsive'
import dayjs from 'dayjs'
import { useMemo } from 'react'
import { useAuth } from '../../../../hooks'

interface FileVersionsModalProps {
	isOpen: boolean
	onClose: () => void
	currentObject: DocumentData | null
	onPreview: (fileId: string, versionId: string) => void
	onCurrentVersionChange?: () => void
}

const FileVersionsModal: FC<FileVersionsModalProps> = ({
	isOpen,
	onClose,
	currentObject,
	onPreview,
	onCurrentVersionChange,
}) => {
	const { isAuthenticated } = useAuth()
	const isMobile = useMediaQuery({ maxWidth: 767 })

	const [selectedVersionId, setSelectedVersionId] = useState<string | null>(
		null,
	)

	const {
		data: fileData,
		isLoading: isLoadingFileData,
		isFetched,
	} = useFile({
		id: currentObject?.id ?? '',
	})

	const sortedVersions = useMemo(() => {
		const items =
			fileData?.data?.versions
				?.slice()
				.sort(
					(a, b) =>
						dayjs(String(b?.createdAt)).valueOf() -
						dayjs(String(a?.createdAt)).valueOf(),
				) ?? []

		return items
	}, [fileData?.data?.versions])

	const currentVersionId = useMemo(() => {
		return fileData?.data?.versions?.find((v) => !!v?.currentVersion)?.id
	}, [fileData?.data?.versions])

	const signedVersionId = useMemo(() => {
		const versions = sortedVersions ?? []
		if (!versions.length) return null

		const currentSigned = versions.find(
			(v) => v?.id === currentVersionId && v?.isSigned,
		)
		if (currentSigned?.id) return currentSigned.id

		return versions.find((v) => v?.isSigned)?.id ?? null
	}, [sortedVersions, currentVersionId])

	const originalVersionBeforeSigningId = useMemo(() => {
		if (!signedVersionId) return null

		const signedVersionIndex = sortedVersions.findIndex(
			(v) => v?.id === signedVersionId,
		)

		if (signedVersionIndex < 0) return null

		return sortedVersions[signedVersionIndex + 1]?.id ?? null
	}, [sortedVersions, signedVersionId])

	useEffect(() => {
		if (isOpen && currentVersionId) {
			if (signedVersionId && currentVersionId !== signedVersionId) {
				setSelectedVersionId(signedVersionId)
			} else {
				setSelectedVersionId(currentVersionId)
			}
		}
	}, [isOpen, currentVersionId, signedVersionId])

	useEffect(() => {
		if (
			isOpen &&
			selectedVersionId &&
			!sortedVersions.some((version) => version.id === selectedVersionId)
		) {
			setSelectedVersionId(currentVersionId ?? null)
		}
	}, [isOpen, selectedVersionId, sortedVersions, currentVersionId])

	const { mutate: rollbackVersion, isPending: isSaving } =
		useRollbackObjectVersion()
	const selectedVersion = sortedVersions.find(
		(version) => version.id === selectedVersionId,
	)
	const isSaveDisabled =
		isSaving ||
		!selectedVersion ||
		selectedVersionId === currentVersionId ||
		(Boolean(signedVersionId) && selectedVersionId !== signedVersionId)

	const handlePreview = (versionId: string) => {
		if (currentObject?.id) {
			onPreview(currentObject.id, versionId)
		}
	}

	const handleClose = () => {
		setSelectedVersionId(null)
		onClose()
	}

	const handleSubmit = async () => {
		if (!isSaveDisabled && isAuthenticated && currentObject?.id) {
			const versionIdToRollback = signedVersionId ?? selectedVersionId
			await rollbackVersion(
				{
					id: currentObject?.id,
					versionId: versionIdToRollback,
				},
				{
					onSuccess: () => {
						onCurrentVersionChange?.()
						handleClose()
					},
				},
			)
		}
	}

	return (
		<Dialog
			isOpen={isOpen}
			onClose={handleClose}
			title="Version history"
			icon={<ManageHistory />}
			className="p-5 max-h-[95%] h-screen max-sm:w-screen sm:w-screen md:h-fit lg:w-[840px] md:w-[563px] md:max-w-[600px]"
			controllers={
				<div className="flex items-center justify-end gap-2">
					<Button
						variant="tertiary"
						onClick={() => {
							if (isMobile) {
								const version = fileData?.data?.versions?.find(
									(v) => v?.currentVersion,
								)
								version && setSelectedVersionId(version?.id)

								return
							}
							handleClose()
						}}
						label={isMobile ? 'Reset' : 'Cancel'}
						className="w-1/2 md:w-fit flex items-center justify-center"
					/>
					<Button
						label="Save"
						onClick={handleSubmit}
						loading={isSaving}
						disabled={isSaveDisabled}
						tooltip={
							!selectedVersion
								? 'Select version'
								: selectedVersionId === currentVersionId
									? 'This version has already been selected'
									: Boolean(signedVersionId) &&
										  selectedVersionId !== signedVersionId
										? "You can't switch to an unsigned version because this file is already signed"
										: null
						}
						tooltipOnDisabled
						className="w-1/2 md:w-fit flex items-center justify-center"
					/>
				</div>
			}
		>
			<div className="h-full md:h-fit">
				<p className="flex items-center gap-2 justify-start text-lg text-text-main dark:text-text-main-dark font-bold mb-3">
					<FileFormatIcon fileName={currentObject?.name ?? ''} />
					{currentObject?.name}
				</p>

				<div className="rounded-lg border border-stroke-light dark:border-stroke-light-dark  min-h-[190px] md:max-h-[280px] box-border relative shadow-lg">
					<div className="h-12 flex items-center justify-start px-5 box-border">
						<h3 className="font-semibold text-base text-text-main dark:text-text-main-dark">
							Versions
						</h3>
					</div>
					{fileData?.data?.versions?.length ? (
						<FileVersionsTable
							versions={sortedVersions}
							onPreview={handlePreview}
							selectedVersionId={selectedVersionId || ''}
							currentVersionId={currentVersionId}
							onSelectVersion={setSelectedVersionId}
							currentObjectId={currentObject?.id}
							originalVersionBeforeSigningId={originalVersionBeforeSigningId}
						/>
					) : (
						isFetched &&
						!isLoadingFileData && (
							<div className="border-t border-stroke-light dark:border-stroke-light-dark  flex items-center justify-center h-32">
								<span className="text-text-secondary italic text-sm">
									No versions
								</span>
							</div>
						)
					)}

					<LockerLoader visible={isLoadingFileData} />
				</div>
			</div>
		</Dialog>
	)
}

export default FileVersionsModal
